// Display vocabulary of the Bulk Enquiry pages.
//
// The backend (backoffice-service modules/enquiry_import) decides every status,
// count, required file, missing file, can_process and blocking reason. This
// module only turns those codes into words and chip colours; nothing here
// derives a status or a count. A code the backend adds later still renders:
// its own name on a gray chip.
import { FILE_KIND_LABEL } from '@src/services/consentSettings'
import ENQUIRY_CATEGORY_LABEL from '@src/services/enquiryCategory'
import { ENQUIRY_STATUS_LABELS, PARK_REASON_LABELS } from '@src/services/enquiryStatus'

// application_enquiry sub-permissions. Every bulk endpoint needs BULK; upload,
// the rows list (they carry e-KTP data) and a row's files need CREATE as well.
export const BULK_PERMISSION = 'upload_enquiry_bulk'
export const CREATE_PERMISSION = 'create_enquiry'

// Upload limit of POST /import-batches (enquiry_import.middlewares XLSX_MAX_BYTES).
export const XLSX_ACCEPT = ['.xlsx']
export const XLSX_MAX_BYTES = 5 * 1024 * 1024

// Files a row can get (POST /:batchId/rows/:rowId/files/:kind), by family.
// Mirrors of the backend, checked before a byte is sent (a refused upload
// would leave an orphan media object): the single flow's uploaders (core
// helpers/upload.js ktpUpload: jpeg/png/webp <= 5 MB; enquiryDocumentUpload:
// pdf/doc/docx <= 10 MB) and the MIME type the backend stores per extension
// (enquiry_import.validation STAGED_MIME_BY_EXT).
export const STAGE_LIMITS = {
  photo: {
    maxBytes: 5 * 1024 * 1024,
    types: { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' },
  },
  document: {
    maxBytes: 10 * 1024 * 1024,
    types: {
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
  },
}

/** "5 MB": a whole-megabyte limit as the upload messages print it. */
export const megabytes = (bytes) => `${Math.round(bytes / (1024 * 1024))} MB`

// Extension of each MIME type a row file may be saved with (STAGE_LIMITS, inverted: jpg for image/jpeg).
const EXT_OF_TYPE = Object.values(STAGE_LIMITS).reduce((map, { types }) => {
  Object.entries(types).forEach(([ext, type]) => {
    if (!map[type]) map[type] = ext
  })
  return map
}, {})

/**
 * The name a saved row file is downloaded under: its own name with the
 * extension of its type. The backend allow-lists a staged file's mime_type,
 * not its name, so a document staged as "x.exe" is never saved as an .exe.
 * Without a known type the name keeps no extension at all.
 */
export function downloadNameOf(fileName, mimeType) {
  const base =
    String(fileName || '')
      .split(/[\\/]/)
      .pop()
      .replace(/\.[^.]*$/, '')
      .trim() || 'file'
  const ext = EXT_OF_TYPE[String(mimeType || '').trim().toLowerCase()]
  return ext ? `${base}.${ext}` : base
}

/** "1 row" / "2 rows". */
export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** "1 file needs" / "2 files need": the count, its noun and the verb agreeing with it. */
export const pluralVerb = (n, one, verbOne, verbMany) => `${plural(n, one)} ${n === 1 ? verbOne : verbMany}`

// One drop / pick at most: 500 rows (BULK_ENQUIRY_MAX_ROWS) x 3 files (TSD §6.9).
export const MAX_DROP_FILES = 1500
// Uploads running at the same time while saving files (TSD §5).
export const STAGE_CONCURRENCY = 3

// Search limit of the list and rows endpoints (enquiry_import.middlewares).
export const SEARCH_MAX_LENGTH = 100

// The rows search is a GET query, so what HR types lands in access logs. A
// NIK never goes there: a search of more digits than this is sent as its
// last NIK_SEARCH_DIGITS digits, which the backend still matches inside the
// NIK (any 4 to 16 digits), and the NIK column shows which row it is.
export const NIK_SEARCH_DIGITS = 6

/**
 * What the rows search sends for what HR typed: `{ term, shortened }`;
 * shortened: a longer number (a NIK) was cut to its last NIK_SEARCH_DIGITS digits.
 */
export function rowSearchTerm(text) {
  const term = String(text || '').trim()
  const digits = term.replace(/\s+/g, '')
  if (/^\d+$/.test(digits) && digits.length > NIK_SEARCH_DIGITS) {
    return { term: digits.slice(-NIK_SEARCH_DIGITS), shortened: true }
  }
  return { term, shortened: false }
}

// Shown where the rows of a batch are refused (404) although the batch loads:
// the bulk grant reaches the batch, the create grant does not reach its company.
export const ROWS_HIDDEN_TEXT =
  'You can see this batch but not its rows: that needs the Create Enquiry permission for its company.'

/** A file named with another person's NIK than the row's (the backend refuses it: 409 file_nik_mismatch). */
export const nikMismatchText = (rowNo) =>
  `The file name carries a NIK that is not row ${rowNo}'s. Choose that person's row, or check the file and its name.`

// Colours are MyChip colours that also have a dot colour (no 'blue').
export const BATCH_STATUS = {
  open: { label: 'Open', color: 'primary' },
  processing: { label: 'Processing', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
  canceled: { label: 'Canceled', color: 'gray' },
}

// Keys in the order the API lists them (enquiry_import.validation ROW_STATUSES).
export const ROW_STATUS = {
  invalid: { label: 'Invalid', color: 'error' },
  incomplete: { label: 'Missing files', color: 'warning' },
  ready: { label: 'Ready', color: 'success' },
  created: { label: 'Created', color: 'primary' },
  failed: { label: 'Failed', color: 'error' },
}

// ready_breakdown of the batch detail: where its ready rows go on Process.
export const READY_BREAKDOWN_LABEL = {
  direct: 'Direct to CLIK',
  invite: 'With consent invitation',
}

// files[kind].status of a row ('not_required' is not shown), plus the two
// states only the file grid shows: a file added in this browser and not saved
// yet, and a saved file its row no longer needs (leftover after the settings
// were re-applied; Process ignores it).
const FILE_STATUS = {
  staged: { label: 'Saved', color: 'success' },
  reused: { label: 'Reused', color: 'success' },
  missing: { label: 'Missing', color: 'error' },
  pending: { label: 'Not saved yet', color: 'warning' },
  leftover: { label: 'Not needed any more', color: 'gray' },
}

/** `{ label, color }` of one file state (a files[kind].status, or 'pending' / 'leftover'). */
export function fileStatusChip(state) {
  return chipOf(FILE_STATUS, state)
}

const kindLabel = (kind) => FILE_KIND_LABEL[kind] || kind

/**
 * Why a dropped file waits for HR's choice instead of going to a row
 * (fileMatcher's unmatched reasons). keyMode: 'nik' | 'email' (fileMatcher.keyMode).
 */
export function trayReasonText({ reason, kind } = {}, { keyMode = 'nik' } = {}) {
  switch (reason) {
    case 'no_key':
      return keyMode === 'nik'
        ? 'The file name has no NIK (16 digits).'
        : 'The file name has no email or Employee ID of a row.'
    case 'unknown_key':
      return keyMode === 'nik'
        ? 'No row waiting for files has this NIK.'
        : 'No row waiting for files has this email or Employee ID.'
    case 'multiple_niks':
      return 'The file name has more than one NIK.'
    case 'ambiguous_key':
      return 'The file name matches more than one row.'
    case 'email_joined':
      return (
        "A row's email is joined to a word in the name (tnc_budi@contoh.com), so it is not taken as that email. " +
        'Name the file with the email, then _ and the word (budi@contoh.com_tnc.pdf), or assign it: its row is suggested.'
      )
    case 'choose_kind':
      return `Choose whether it is the ${kindLabel('terms_condition')} or the ${kindLabel('job_application_form')}.`
    case 'duplicate_target':
      return `Another file you added is also for this row's ${kindLabel(kind)}: choose which one to use.`
    case 'not_needed_for_row':
      return kind ? `${kindLabel(kind)} is not needed for this row.` : 'This row needs no document.'
    case 'ktp_reused':
      return "This row reuses the employee's e-KTP on file: no photo is needed."
    default:
      return reason ? String(reason) : ''
  }
}

/** Why a file cannot be used in this batch at all (fileMatcher's rejected reasons). */
export function rejectReasonText({ reason, family, kind } = {}) {
  switch (reason) {
    case 'junk':
      return 'This is a system file, not a photo or a document.'
    case 'heic':
      return 'HEIC photos are not accepted: save the photo as JPG or PNG.'
    case 'empty':
      return 'The file is empty.'
    case 'too_large':
      return family === 'photo'
        ? `Larger than ${megabytes(STAGE_LIMITS.photo.maxBytes)}, the KTP photo limit.`
        : `Larger than ${megabytes(STAGE_LIMITS.document.maxBytes)}, the document limit.`
    case 'unsupported_type':
      return 'Not a KTP photo (JPG, PNG, WEBP) or a document (PDF, DOC, DOCX).'
    case 'wrong_type':
      if (kind === 'ktp_photo') return 'A KTP photo must be a JPG, PNG or WEBP image.'
      if (family === 'photo') {
        return `${kind ? `The ${kindLabel(kind)}` : 'A document'} must be a PDF, DOC or DOCX file, not a photo.`
      }
      return 'This file must be a PDF, DOC or DOCX document.'
    case 'kind_not_needed':
      return kind ? `${kindLabel(kind)} is not needed in this batch.` : 'This batch needs no document from HR.'
    default:
      return reason ? String(reason) : ''
  }
}

// blocking_reasons of the batch detail, and the code of a refused Process
// (enquiry_import.controller prepareProcess). no_subscription is the
// backend's fundingBlockReason: PAYG passes; refused are a suspended or
// unpaid subscription, a company with no priceable plan, and a company still
// holding quota from a plan that ended (the quota leg of a charge needs an
// active subscription).
export const BLOCKING_REASON_LABEL = {
  inactive: "The company's membership is not active.",
  no_subscription:
    'The company cannot be charged for enquiries yet: no active plan, its subscription is suspended or awaiting payment, or it still has quota left from a plan that ended.',
  billing_unavailable: 'The subscription could not be checked right now. Try again later.',
  consent_config_changed:
    "The company's consent settings changed after this file was uploaded. Apply the current settings first.",
  no_ready_rows: 'No row is ready.',
  processing: 'The batch is being processed.',
  completed: 'The batch is completed.',
  canceled: 'The batch was canceled.',
}

/** `{ label, color }` of `code` in one of the maps above. */
export function chipOf(map, code) {
  return map[code] || { label: code ? String(code) : '—', color: 'gray' }
}

// funding{} of a batch (enquiry_import FUNDING_BUCKETS: the enquiries its
// created rows became, read live, so a Pending Payment paid later turns
// Funded) and the `funding` of a Process progress event (funded |
// pending_payment | parked | canceled). The words are Report Enquiry's.
export const FUNDING = {
  funded: { label: 'Funded', color: 'success' },
  pending_payment: { label: PARK_REASON_LABELS.pending_payment, color: 'warning' },
  parked: { label: PARK_REASON_LABELS.parked_at_create, color: 'warning' },
  canceled: { label: ENQUIRY_STATUS_LABELS.canceled, color: 'error' },
  removed: { label: 'Deleted', color: 'gray' },
  superseded: { label: 'Replaced by a later request', color: 'gray' },
}

const FUNDING_ORDER = Object.keys(FUNDING)
const fundingRank = (bucket) => {
  const i = FUNDING_ORDER.indexOf(bucket)
  return i < 0 ? FUNDING_ORDER.length : i
}

/** The non-zero buckets of a funding{} map, in FUNDING order: [{ bucket, count, label, color }]. */
export function fundingChips(funding) {
  return Object.entries(funding || {})
    .filter(([, n]) => Number(n) > 0)
    .sort(([a], [b]) => fundingRank(a) - fundingRank(b))
    .map(([bucket, n]) => ({ bucket, count: Number(n), ...chipOf(FUNDING, bucket) }))
}

// outcome_error of a created row (enquiry_import.validation fundingOutcome /
// dispatchOutcome): "<code>: <the backend's reason>", several joined by " | ".
// The row is an enquiry either way; only `unfundable` means it was withdrawn.
// The notes say what happened when the row was processed (outcome_error stays
// on the row); the enquiry's own status next to them says where it is now.
const OUTCOME = {
  unfundable: { tone: 'error', label: 'Withdrawn when created: the company could not be charged.' },
  billing_unavailable: {
    tone: 'info',
    label: 'Created on hold: billing could not be reached then. It is funded automatically.',
  },
  funding_error: { tone: 'info', label: 'Created on hold: it could not be funded then. It is funded automatically.' },
  dispatch_deferred: { tone: 'info', label: 'Not sent to CLIK when created: it is sent automatically within a minute.' },
}

/**
 * The notes of a created row's outcome_error: [{ code, tone: 'error' | 'info',
 * label, detail }] (detail = the backend's reason). A text without a known
 * code is shown as it is, as an error.
 */
export function outcomeNotes(outcomeError) {
  if (!outcomeError) return []
  return String(outcomeError)
    .split(' | ')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = /^([a-z_]+):\s*([\s\S]*)$/.exec(part)
      const known = match ? OUTCOME[match[1]] : null
      if (!known) return { code: null, tone: 'error', label: part, detail: null }
      return { code: match[1], tone: known.tone, label: known.label, detail: match[2] || null }
    })
}

/** The toast of POST /:batchId/retry-failed ({ retried, now_ready, now_incomplete, now_invalid, reopened, completed }). */
export function retrySummaryText(result) {
  const { retried = 0, now_ready: ready = 0, now_incomplete: incomplete = 0, now_invalid: invalid = 0 } = result || {}
  if (!retried) return 'No failed row to check again.'
  let text = `${plural(retried, 'failed row')} checked again: ${ready} ready, ${incomplete} missing files, ${invalid} invalid.`
  if (result.reopened) text += ' The batch is open again.'
  else if (result.completed) text += ' Nothing is left to process: the batch is completed.'
  return text
}

/** The toast of DELETE /:batchId/rows/:rowId ({ row_no, rechecked_rows, batch_status }). */
export function removedRowText(result) {
  const { row_no: rowNo, rechecked_rows: rechecked = [], batch_status: batchStatus } = result || {}
  let text = rowNo ? `Row ${rowNo} removed.` : 'Row removed.'
  if (rechecked.length) text += ` ${plural(rechecked.length, 'other row')} checked again.`
  if (batchStatus === 'completed') text += ' Nothing is left to process: the batch is completed.'
  return text
}

/**
 * "KTP photo 3 · Terms and condition 2" from a `{ kind: count }` map (missing_breakdown),
 * zero counts left out; '' when nothing is left.
 */
export function breakdownText(counts, labels) {
  return Object.entries(counts || {})
    .filter(([, n]) => Number(n) > 0)
    .map(([key, n]) => `${labels[key] || key} ${n}`)
    .join(' · ')
}

/**
 * The batch's `required_files` ({ ktp_photo: bool, terms_condition: bool,
 * job_application_form: 'candidate' | null }) as labels.
 */
export function requiredFileLabels(requiredFiles) {
  return Object.entries(requiredFiles || {})
    .filter(([, required]) => required)
    .map(([kind, required]) =>
      required === 'candidate'
        ? `${FILE_KIND_LABEL[kind] || kind} (candidates)`
        : FILE_KIND_LABEL[kind] || kind
    )
}

/** Which categories the batch's `direct_to_clik` ({ candidate, employee }) sends without an invitation. */
export function directToClikLabel(directToClik) {
  const categories = Object.entries(directToClik || {})
    .filter(([, direct]) => direct)
    .map(([category]) => `${ENQUIRY_CATEGORY_LABEL[category] || category}s`)
  return categories.length ? categories.join(' and ') : null
}

/** The file kinds a row needs or holds (every status but 'not_required'), for its file chips. */
export function rowFileChips(files) {
  return Object.entries(files || {})
    .filter(([, file]) => file && file.status !== 'not_required')
    .map(([kind, file]) => {
      const { label, color } = chipOf(FILE_STATUS, file.status)
      return { kind, label: `${FILE_KIND_LABEL[kind] || kind}: ${label}`, color, url: file.url || null }
    })
}

/**
 * Whether the company's consent settings changed since the batch was uploaded:
 * the backend's consent_changes list, or its consent_config_changed blocking
 * reason (the same test on the backend), so "Apply current settings" is also
 * offered by a backend that does not send consent_changes yet.
 */
export function consentSettingsChanged(batch) {
  return (batch?.consent_changes || []).length > 0 || (batch?.blocking_reasons || []).includes('consent_config_changed')
}

/**
 * The "Rows" figure of a batch: its live rows (counts.total), except for a
 * canceled batch. Cancel removed every row that was not an enquiry yet, so its
 * live count says nothing about the upload; it shows the rows its file had
 * (total_rows, kept by the backend).
 */
export function batchRowTotal(batch) {
  if (batch?.status === 'canceled' && batch.total_rows != null) return batch.total_rows
  return batch?.counts?.total ?? 0
}

// Template v2 column (header) of each row field the rows table shows. An
// invalid row stores none of its identity (nik is null) but keeps `raw`: every
// cell as HR typed it, keyed by column header, the same name its
// errors[].field carries ("Row" names a row-level error such as a duplicate).
const FIELD_COLUMN = {
  category: 'Category',
  full_name: 'Full Name',
  email: 'Email',
  employee_code: 'Employee ID',
  nik: 'NIK',
}

/** What HR typed in `column` (a template header) of an invalid row, as text; null when the row keeps no raw or the cell was empty. */
export function typedValue(row, column) {
  const value = row?.raw?.[column]
  return value === null || value === undefined || value === '' ? null : String(value)
}

/**
 * `{ text, invalid }` of one row field for the rows table. `invalid`: its
 * column has an error; the text is then what HR typed (the rejected value),
 * else the stored value, else (an invalid row stores no NIK) what was typed.
 * `typed: false` never falls back to the typed cell: the NIK of a batch whose
 * candidates upload their own e-KTP (needs_hr_ktp false), where the backend
 * ignored the e-KTP columns and no row keeps a NIK.
 */
export function rowField(row, field, { typed: useTyped = true } = {}) {
  const column = FIELD_COLUMN[field]
  const invalid = Boolean(column) && (row?.errors || []).some((e) => e.field === column)
  const typed = column && useTyped ? typedValue(row, column) : null
  const stored = row?.[field] === null || row?.[field] === undefined || row?.[field] === '' ? null : String(row[field])
  return { text: (invalid && typed) || stored || typed, invalid }
}

/** A row's errors, each with `typed`: what HR typed in the column it names (null for a row-level error or an empty cell). */
export function errorsWithTyped(row) {
  return (row?.errors || []).map((e) => ({ ...e, typed: typedValue(row, e.field) }))
}
